import type i18nInstance from "i18next";
import { useEffect, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { CustomIcons } from "../../components/Icons/Custom";
import { clearAdIntent, saveAdIntent } from "../../hooks/AdIntent";
import SymbolKeepoIcon from "../../icons/SymbolKeepo";
import { classNames } from "../../utils/styles";
import { useRegister } from "../Register/useRegister";
import ExampleKeepo, { ExampleKeepoContent, ExampleKeepoStyle } from "./ExampleKeepo";
import { AD_NICHE_COPY, AdNicheCopy } from "./nicheCopy";
import { AD_NICHE_COPY_EN } from "./nicheCopy.en";
import { adNicheBySlug } from "./niches";
import * as S from "./styles";

type Props = { fromAd?: boolean };

// The sign up: /register and the ad pages /comecar and /comecar/:nicho, with the niche's own text, colors and
// Keepo. The ad pages (`fromAd`) are always in Portuguese, the sign up's own fields too, through a Portuguese copy
// of the translations: the app's language stays the browser's (the new account is saved in it). They keep the
// niche for after the sign up; /register is the generic page in the person's language and drops it.
// One copy for the whole app, made once: a copy takes a moment to load, so the page suspends until it's ready, and
// React throws away what it hadn't shown yet and renders again. A copy made in the component (useState) would be made
// again on every try, never ready: a blank page that never opens.
let portugueseCopy: ReturnType<typeof i18nInstance.cloneInstance> | undefined;
const portugueseOf = (i18n: typeof i18nInstance) => {
  if (!portugueseCopy) {
    // no `lng` here: cloning with one already changes the language (and the detector saves it) while it initializes
    const copy = i18n.cloneInstance();
    // the copy shares the browser's language detector, which would save "pt" as the person's language
    // (localStorage), making every later visit and the new account Portuguese
    (copy.services as { languageDetector?: unknown }).languageDetector = undefined;
    copy.changeLanguage("pt");
    portugueseCopy = copy;
  }
  return portugueseCopy;
};

const PortugueseSignupPage = () => {
  const { i18n } = useTranslation();
  const portuguese = portugueseOf(i18n);
  return (
    <I18nextProvider i18n={portuguese}>
      <SignupPage fromAd />
    </I18nextProvider>
  );
};

// `?lng=en` (the site's English niche pages link so): the ad page in English, the app in English too (the browser's
// language detector reads the same parameter); without it the ad pages stay Portuguese whatever the browser's
const asksForEnglish = (params: URLSearchParams) => (params.get("lng") ?? "").toLowerCase().startsWith("en");

const AdSignupPage = ({ fromAd = true }: Props) => {
  const [params] = useSearchParams();
  if (!fromAd) return <SignupPage fromAd={false} />;
  return asksForEnglish(params) ? <SignupPage fromAd english /> : <PortugueseSignupPage />;
};

const SignupPage = ({ fromAd = true, english = false }: Props & { english?: boolean }) => {
  const { nicho } = useParams();
  const niche = adNicheBySlug(fromAd ? nicho : null);
  const { t } = useTranslation();
  const { step, email, errorMessages, stepForm, startFederatedLogin, editEmail } = useRegister({
    emailButtonLabel: t("pages.adSignup.card.button"),
    emailButtonAlwaysOn: true,
  });

  useEffect(() => {
    if (fromAd) saveAdIntent(niche.slug);
    else clearAdIntent();
  }, [fromAd, niche.slug]);

  // The phone shows one of the two looks of the example Keepo, drawn at random on each visit
  const [look] = useState<ExampleKeepoStyle>(() => (Math.random() < 0.5 ? "links" : "site"));
  // a niche's text: Portuguese (nicheCopy.ts), or English when asked (nicheCopy.en.ts); the generic page's is translated
  const copy: AdNicheCopy = niche.slug
    ? (english ? AD_NICHE_COPY_EN : AD_NICHE_COPY)[niche.key]
    : {
        title: t("pages.adSignup.generic.title"),
        subtitle: t("pages.adSignup.generic.subtitle"),
        example: t("pages.adSignup.generic.example", { returnObjects: true }) as unknown as ExampleKeepoContent,
      };
  const example = copy.example;

  // The tab's title and the search description are the niche's (the ad page's), back as they were on leaving
  useEffect(() => {
    if (!fromAd) return;
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    document.title = `${copy.title} | Keepo`;
    if (description) description.content = copy.subtitle;
    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) description.content = previousDescription;
    };
  }, [fromAd, copy.title, copy.subtitle]);

  const stats = [
    ["time", "timeLabel"],
    ["price", "priceLabel"],
    ["users", "usersLabel"],
  ];

  return (
    <S.Page $colors={niche.colors}>
      <S.Layout>
        <div>
          <S.Logo>
            <SymbolKeepoIcon />
          </S.Logo>
          {/* "1 minuto" never splits across lines (the last space of the title is a non-breaking one) */}
          <S.Title>{copy.title.replace(/ (?=\S+$)/, "\u00a0")}</S.Title>
          <S.Subtitle>{copy.subtitle}</S.Subtitle>
          <S.Stats>
            {stats.map(([value, label]) => (
              <div key={value}>
                <dt>{t(`pages.adSignup.stats.${label}`)}</dt>
                <dd>{t(`pages.adSignup.stats.${value}`)}</dd>
              </div>
            ))}
          </S.Stats>
        </div>
        <S.Stage>
          <S.Phone aria-hidden="true">
            <div>
              <ExampleKeepo content={example} colors={niche.colors} look={look} />
            </div>
          </S.Phone>
          <S.Card className="card">
            <S.Steps role="img" aria-label={t("pages.adSignup.card.step", { step })}>
              {[1, 2, 3].map(n => (
                <span key={n} className={n <= step ? "done" : undefined} />
              ))}
            </S.Steps>
            <S.CardTitle>
              {step === 3
                ? t("pages.register.step3Title")
                : step === 2
                  ? t("pages.adSignup.card.step2Title")
                  : t("pages.adSignup.card.title")}
            </S.CardTitle>
            <p className="typography-p mb-5 mt-1 !text-shark-600">
              {step === 3
                ? t("pages.register.step3Description").replace("${email}", email)
                : step === 2
                  ? email
                  : t("pages.adSignup.card.lead")}
              {step === 2 && (
                <button
                  type="button"
                  onClick={editEmail}
                  aria-label={t("pages.adSignup.card.editEmailLabel")}
                  className="ml-2 font-semibold underline underline-offset-2">
                  {t("pages.adSignup.card.editEmail")}
                </button>
              )}
            </p>
            {errorMessages.length > 0 && (
              <div className="mb-4 space-y-1">
                {errorMessages.map(errorMessage => (
                  <p key={errorMessage} className="typography-p text-red-500">
                    {errorMessage}
                  </p>
                ))}
              </div>
            )}
            {stepForm}
            {/* e-mail first; Google and Facebook as smaller options under it */}
            {step === 1 && (
              <>
                <S.Divider>{t("pages.adSignup.card.orSocial")}</S.Divider>
                <S.Socials>
                  <button type="button" onClick={() => startFederatedLogin("Google")}>
                    <CustomIcons.Google />
                    {t("pages.adSignup.card.google")}
                  </button>
                  <button type="button" onClick={() => startFederatedLogin("Facebook")}>
                    <CustomIcons.Facebook />
                    {t("pages.adSignup.card.facebook")}
                  </button>
                </S.Socials>
              </>
            )}
            {step !== 3 && (
              <div
                className={classNames("text-center text-xs leading-5 text-shark-500", {
                  "mt-7": step === 1,
                  "mt-1": step !== 1,
                })}>
                <p>
                  {t("pages.register.registerButton.byContinuing")}
                  {/* the links on their own line, easier to read */}
                  <br />
                  <a
                    href="https://www.keepo.bio/termos-de-uso/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2">
                    {t("pages.register.registerButton.terms")}
                  </a>{" "}
                  {t("pages.register.registerButton.and")}{" "}
                  <a
                    href="https://www.keepo.bio/politica-de-privacidade/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2">
                    {t("pages.register.registerButton.privacy")}
                  </a>
                </p>
                {/* on step 2 the e-mail is already known to be new */}
                {step === 1 && (
                  <p className="mt-3 font-normal !text-shark-900">
                    {t("pages.register.registerButton.hasAccoutn")}{" "}
                    <Link to="/login" className="underline underline-offset-2">
                      {t("pages.register.registerButton.linkText")}
                    </Link>
                  </p>
                )}
              </div>
            )}
          </S.Card>
        </S.Stage>
      </S.Layout>
    </S.Page>
  );
};

export default AdSignupPage;
